import {
  LegalPageLayout,
  LegalSection as Section,
  type LegalSectionLink,
} from '@/components/public/LegalPageStructure';

const LAST_UPDATED = {
  dateTime: '2026-05-20',
  display: 'May 20, 2026',
} as const;

const PRIVACY_SECTIONS = [
  { id: 'what-we-collect', label: 'What we collect' },
  { id: 'what-we-do-not-collect', label: "What we don't collect" },
  { id: 'third-party-services', label: 'Third-party services' },
  { id: 'data-sources', label: 'Data sources' },
  { id: 'data-retention', label: 'Data retention' },
  { id: 'security', label: 'Security' },
  { id: 'your-rights', label: 'Your rights' },
  { id: 'changes', label: 'Changes to this policy' },
  { id: 'contact', label: 'Contact' },
] as const satisfies readonly LegalSectionLink[];

export function Privacy() {
  return (
    <LegalPageLayout
      page="privacy"
      title="Privacy Policy"
      dates={[{ label: 'Last updated', ...LAST_UPDATED }]}
      sections={PRIVACY_SECTIONS}
      intro={
        <p>
            Prometheus Legal Intelligence is a Claude connector that returns
            primary-law answers from a public-record corpus. This policy
            describes what data the connector handles, what it does not handle,
            and the choices you have. It applies to the connector endpoint at{' '}
            <code className="break-all rounded-sm bg-surface-1 px-1 font-mono text-sm">
              prometheus-connector
            </code>{' '}
            and the marketing pages at this site.
        </p>
      }
    >
          <Section id="what-we-collect" n={1} title="What we collect">
            <p>The connector collects the minimum data necessary to answer your query:</p>
            <ul>
              <li>
                <strong>OAuth identity.</strong> When Claude connects to
                Prometheus, the OAuth 2.1 flow exchanges an access token. We
                store the token and the associated account identifier so the
                connector can authenticate subsequent requests on your behalf.
              </li>
              <li>
                <strong>Transient query embeddings.</strong> To search the
                corpus, your query is converted to a numeric embedding vector
                via a third-party embedding model (see Section 3). The vector
                is used to run a similarity search and is then discarded.
              </li>
              <li>
                <strong>Aggregate usage metrics.</strong> To understand which
                material is most useful and to keep the service reliable, we
                may keep aggregate, anonymized usage counts &mdash; for example,
                how often each tool is invoked. These counts are not tied to
                your identity or to the content of your queries. We do not
                keep a per-request log of your activity, and we do not log
                query text.
              </li>
            </ul>
          </Section>

          <Section id="what-we-do-not-collect" n={2} title="What we don't collect">
            <p>The connector does not retain:</p>
            <ul>
              <li>The text of your queries.</li>
              <li>Your Claude chat history or any prior turns in a conversation.</li>
              <li>Files you have attached or shared in Claude.</li>
              <li>The full response body returned to Claude.</li>
              <li>Any data sufficient to reconstruct your research session.</li>
            </ul>
            <p>
              The query text exists only in memory long enough to be embedded
              and matched against the corpus. The embedding and the matched
              passages are returned to Claude and then released.
            </p>
          </Section>

          <Section id="third-party-services" n={3} title="Third-party services">
            <p>
              Prometheus relies on three third-party services to deliver
              answers. Each is bound by its own terms and privacy commitments,
              and we share with them only what is required for the function
              they perform:
            </p>
            <ul>
              <li>
                <strong>OpenAI (embedding).</strong> Your query text is sent to
                OpenAI's embedding API to produce a numeric vector. OpenAI's
                API terms prohibit training on data submitted through the API.
              </li>
              <li>
                <strong>Supabase (infrastructure).</strong> The corpus,
                embeddings, OAuth identity records, and connector compute run
                on Supabase. Supabase processes data on our behalf under its
                standard data processing terms.
              </li>
              <li>
                <strong>Anthropic (Claude).</strong> The connector is invoked
                by Claude on your behalf. Anthropic's privacy policy governs
                the data Claude itself processes, including the chat you have
                with it. We do not share data with Anthropic beyond the
                response payload the connector returns to your Claude session.
              </li>
            </ul>
          </Section>

          <Section id="data-sources" n={4} title="Data sources">
            <p>
              The corpus is built from public-record legal materials. We do not
              use proprietary headnotes, summaries, or annotations that
              third-party publishers claim copyright in. Specifically:
            </p>
            <ul>
              <li>
                <strong>Court opinions and dockets</strong> from{' '}
                <a
                  href="https://www.courtlistener.com/"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="CourtListener (opens in a new tab)"
                >
                  CourtListener
                </a>
                's bulk data exports, which are themselves drawn from public
                court records.
              </li>
              <li>
                <strong>Federal statutes and regulations</strong> from{' '}
                <a
                  href="https://www.govinfo.gov/"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="GovInfo (opens in a new tab)"
                >
                  GovInfo
                </a>{' '}
                (United States Code, Code of Federal Regulations, Federal
                Register).
              </li>
              <li>
                <strong>Patent prosecution and grant data</strong> from the{' '}
                <a
                  href="https://www.uspto.gov/"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="USPTO (opens in a new tab)"
                >
                  USPTO
                </a>
                's Open Data Portal and PatentsView.
              </li>
              <li>
                <strong>Securities filings</strong> from the SEC's{' '}
                <a
                  href="https://www.sec.gov/edgar"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="EDGAR (opens in a new tab)"
                >
                  EDGAR
                </a>{' '}
                system.
              </li>
              <li>
                <strong>State statutes and codes</strong> from the official
                publishers of each jurisdiction's primary law. Following{' '}
                <em>Veeck v. Southern Building Code Congress International</em>,
                293 F.3d 791 (5th Cir. 2002) (en banc), the operative text of an enacted
                law is in the public domain.
              </li>
            </ul>
          </Section>

          <Section id="data-retention" n={5} title="Data retention">
            <p>
              We do not retain query text, per-request activity logs, or any
              inferred content from your usage. OAuth identity records (your
              email and a hashed user identifier) persist for the lifetime of
              your account so we can enforce rate limits and distinguish
              free- from pro-tier users; you can delete your account at any
              time by contacting us and we'll purge associated records within
              30 days. Any aggregate usage counts we keep are anonymized and
              not linked to you.
            </p>
          </Section>

          <Section id="security" n={6} title="Security">
            <p>
              All traffic between Claude and the connector is carried over
              HTTPS. The OAuth 2.1 flow uses PKCE (Proof Key for Code Exchange)
              to prevent authorization-code interception. Authorization codes
              are single-use and consumed atomically. Tokens are stored in a
              managed database with row-level security policies that restrict
              read access to the connector function on a per-account basis.
            </p>
            <p>
              No system is perfectly secure. If we become aware of a breach
              affecting your data, we will notify you at the email associated
              with your Claude account.
            </p>
          </Section>

          <Section id="your-rights" n={7} title="Your rights">
            <p>You can:</p>
            <ul>
              <li>
                <strong>Access:</strong> ask for a copy of the OAuth identity
                row we hold for your account.
              </li>
              <li>
                <strong>Delete:</strong> ask us to delete your account record.
                Revoking the connector in Claude accomplishes the same thing.
              </li>
              <li>
                <strong>Portability:</strong> request the data above in a
                machine-readable form.
              </li>
              <li>
                <strong>Object:</strong> object to any processing you believe
                falls outside what this policy describes.
              </li>
            </ul>
            <p>
              Requests should be made to the contact address in Section 9. We
              will respond within 30 days.
            </p>
          </Section>

          <Section id="changes" n={8} title="Changes to this policy">
            <p>
              If we change this policy in a way that materially expands the
              data we collect or how we use it, we will update the "Last
              updated" date at the top of this page and provide notice through
              the connector itself (for example, by surfacing a notice the
              first time you invoke a tool after the change). Minor wording
              changes that do not alter substance may be made without notice.
            </p>
          </Section>

          <Section id="contact" n={9} title="Contact">
            <p>
              Questions about this policy or about the data we hold can be
              sent to{' '}
              <a href="mailto:adrian@prometheuslaw.app">adrian@prometheuslaw.app</a>
              .
            </p>
          </Section>
    </LegalPageLayout>
  );
}
